<template>
  <div>
    <div v-if="isShowAchPayment" class="grid grid-cols-2 gap-4 mb-5">
      <PaymentOptionsButton 
        :is-select="paymentMethod === 'card'" 
        :img-src="creditCardSvg"
        :content="'Credit Card or Debit Card'"
        @click="paymentMethod = 'card'"
      />
      <PaymentOptionsButton 
        :is-select="paymentMethod === 'ach'" 
        :img-src="bankSvg"
        :content="'US Bank Account'"
        @click="switchToAchPayment"
      />
    </div>
    <Ach 
      v-if="paymentMethod === 'ach'"
      :account-role="isClinic ? 'clinic' : 'customer'" 
      :account-id="isClinic ? clinicId : customerId" 
      :charge-amount="chargeAmount"
      :sample-list="sampleList"
      :clinic-id="clinicId"
      :customer-id="customerId"
      :currency="currency"
      :mode="mode"
      :form-id="mode === 'payment' ? 'payment-form' : `${tab}PaymentMethod`"
      :order-price-list="orderPriceList"
      :pay-order-without-apply="payOrderWithoutApply"
      @completeAchPayment="completeAchPayment"
    />
    
    <div v-if="paymentMethod === 'card'">
      <!-- customer net pay -->
      <div 
        v-if="netPayInterval > 0" 
        class="mt-2 flex items-center cursor-pointer"
        @click="handleUseNetPay" 
      >
        <img 
          v-if="isUseNetPay" 
          src="@/assets/orderTest/Checkbox(all).svg" 
          alt="check box"
        >
        <span v-else class="material-icons-outlined customer-credit-uncheck-box block">
          check_box_outline_blank
        </span>
        <p class="ml-2">Use your Net Pay method</p>
      </div>
      <!-- customer credit -->
      <div v-if="customerCredit > 0 && !isUseNetPay && chargeAmount >= 0 && chargeItemCount > 0" class="mt-2">
        <ApplyCredit 
          :available-credit="customerCredit" 
          :currency="currency" 
          @apply-credit="handleApplyCredit"
          @reset-credit="handleResetCredit"
        />
      </div>
      <div
        v-show="!isAddNewCard" 
        v-loading="getCreditCardsLoading"
        :element-loading-text="'Loading Credit Cards...'"
      >
        <div v-if="mode !== 'payment'  || (chargeAmount > 0 && !isUseNetPay)" class="mt-3.5">
          <NoCardOnFile 
            v-show="creditCardList.length === 0" 
            class="mb-2"
            :disabled="mode === 'display'"
            @click="isAddNewCard = true"
          />
          <div 
            v-for="card in displayedCards" 
            :key="card.id" 
            class="mb-2" 
            @click="handleClickCard(card)"
          > 
            <CreditCard
              :card="card"
              :is-clinic="isClinic"
              :is-clinic-card="card.isClinicCard"
              :is-show-edit="isShowEditAndDelete(card)"
              :is-show-delete="isShowEditAndDelete(card)"
              :account-id="isClinic ? clinicId : customerId"
              :account-role="isClinic ? 'clinic' : 'customer'"
              :clinic-id="clinicId"
              :is-disabled="mode === 'display' || (mode !== 'payment' && tab === 'client' && card.isClinicCard)"
              @removeCard="removeCard"
              @updateCard="updateCard"
            />
          </div>
        </div>
        <div 
          v-show="mode !== 'payment' || (chargeAmount > 0 && !isUseNetPay)"
          class="flex justify-center mt-2"
        >
          <AddNewCardButton
            :disabled="mode === 'display'"
            @click="isAddNewCard = true" 
          />
        </div>
        <slot></slot>
        <NewLibraryButton
          v-if="mode === 'payment'"
          color-type="dark-blue"
          :loading="payWithSavedCardLoading"
          :disabled="chargeAmount > 0 && creditCardList.length === 0 && !isUseNetPay"
          :class="[
            isBottom ? 'bottom-fixed' : 'w-full mt-3.5'
          ]"
          @click="completePaymentWithSavedCard"
        >
          {{ isUseNetPay 
            ? 'Confirm' 
            : `Complete Payment ${currency.symbol + chargeAmount.toFixed(2)}` 
          }}
        </NewLibraryButton>
      </div>
      <div v-show="isAddNewCard" class="w-11/12 mt-3.5">
        <StaxPaymentForm 
          v-if="isUsCustomer"
          :card-holder-details="{ customer_id: customerStaxIdToken }"
          :charge-item-count="chargeItemCount"
          :charge-amount="chargeAmount"
          :currency="currency"
          :show-save-card="mode === 'payment'"
          :tab="tab"
          :mode="mode"
          show-back-button
          :account-id="isClinic ? clinicId : customerId"
          :account-type="isClinic ? 'clinic' : 'customer'"
          :clinic-id="clinicId"
          @tokenize="tokenizeCard"
          @cancelPayment="isAddNewCard = false"
        />
        <StripePaymentForm 
          v-else
          :charge-amount="chargeAmount"
          :account-id="isClinic ? clinicId : customerId"
          :account-type="isClinic ? 'clinic' : 'customer'"
          :sample-list="sampleList"
          :sample-id="sampleId"
          :order-id="orderId"
          :need-sample-id="needSampleId"
          :stripe-account="stripeAccount"
          :clinic-id="clinicId"
          :currency="currency"
          show-back-button
          :show-save-card="mode === 'payment'"
          :charge-type="chargeType"
          :mode="mode"
          :order-price-list="orderPriceList"
          :pay-order-without-apply="payOrderWithoutApply"
          :is-use-customer-credit="isUseCustomerCredit"
          :customer-credit="customerCredit"
          @cancelPayment="isAddNewCard = false"
          @completePayment="completeStripePayment"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PaymentOptionsButton from "./PaymentOptionsButton.vue";
import Ach from "./ach/index.vue";
import AddNewCardButton from "./AddNewCardButton.vue";
import CreditCard from "./CreditCard.vue";
import StaxPaymentForm from "../StaxPaymentForm.vue";
import NoCardOnFile from "./NoCardOnFile.vue";
import StripePaymentForm from "../StripePaymentForm.vue";
import NewLibraryButton from "@/NewLibrary/Button.vue";
import { getSavedPaymentsApi, chargeApi, createPaymentIntent, payOrdersApi, getCustomerChargeInfoApi, getCustomerTokenApi } from "@/api/va-order-page/newChargingService.js";
import { generateOrderIdApi } from "@/api/orderkit";
import { getChargeDetailsForPayOrdersApi, getOrderSamplePriceMaps } from "..";
import ApplyCredit from "@/views/order-test/checkout-page/ApplyCredit.vue";
import { notify } from '@/utils/notify';

export default {
  components: {
    PaymentOptionsButton,
    Ach,
    AddNewCardButton,
    CreditCard,
    StaxPaymentForm,
    NoCardOnFile,
    StripePaymentForm,
    NewLibraryButton,
    ApplyCredit,
  },

  props: {
    customerId: {
      type: Number,
      default: null
    },
    isClinic: {
      type: Boolean,
      default: false
    },
    netPayInterval: {
      type: Number,
      default: 0
    },
    customerCredit: {
      type: Number,
      default: 0
    },
    chargeItemCount: {
      type: Number,
      default: 0
    },
    chargeAmount: {
      type: Number,
      default: 0
    },
    isShowAchPayment: {
      type: Boolean,
      default: true
    },
    sampleList: {
      type: Array,
      default: () => []
    },
    isUsCustomer: {
      type: Boolean,
      default: true
    },
    sampleId: {
      type: String,
      default: null
    },
    orderId: {
      type: String,
      default: null
    },
    needSampleId: {
      type: Boolean,
      default: true
    },
    stripeAccount: {
      type: String,
      default: ""
    },
    clinicId: {
      type: Number,
      default: null
    },
    currency: {
      type: Object,
      default: () => ({ name: "USD", symbol: "$"})
    },
    mode: {
      type: String,
      default: "payment"
    },
    tab: { // required for setting page
      type: String,
      default: "client"
    },
    chargeType: {
      type: String,
      default: "testorder"
    },
    orderPriceList: {
      type: Array,
      default: () => []
    },
    payOrderWithoutApply: {
      type: Boolean,
      default: false
    },
    usedCredit: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      creditCardSvg: require("@/assets/orderTest/credit_card.svg"),
      bankSvg: require("@/assets/orderTest/bank.svg"),
      paymentMethod: "card",
      isUseNetPay: false,
      customerStaxIdToken: "",
      creditCardList: [],
      isAddNewCard: false,
      getCreditCardsLoading: false,
      payWithSavedCardLoading: false,
      stripeCardPaymentInfo: null
    }
  },
  computed: {
    isBottom() {
      return document.location.href.includes('/ordertest') 
        && !document.location.href.includes('order-center');
    },
    isUseCustomerCredit() {
      return this.usedCredit > 0;
    },
    displayedCards() {
      let displayedCards = [];
      if (this.mode === "payment") {
        displayedCards = this.creditCardList;
      } else {
        // for other modes
        if (this.tab === "client") {
          displayedCards = this.creditCardList.filter(c => !c.isClinicCard);
        } else {
          displayedCards = this.creditCardList.filter(c => c.isClinicCard);
        } 
      }
      if (displayedCards.length > 0) {
        displayedCards[0].is_select =  true;
        // make sure only the first one is select
        for (let i = 1; i < displayedCards.length; i++) {
          displayedCards[i].is_select = false;
        }
      }
      return displayedCards;
    }
  },
  watch: {
    netPayInterval(newVal) {
      if (newVal > 0) {
        this.isUseNetPay = true;
      }
    }
  },

  emits: ["completePayment", "changeUseCustomerCredit", "changeUseNetPay"],
  
  mounted() {
    this.fetchData();
    if (this.netPayInterval > 0) {
      this.isUseNetPay = true;
    }
  },

  methods: {
    fetchData() {
      // console.log("fetchData")
      if (this.isUsCustomer) {
        // get customer stax id token
        this.getCustomerStaxIdToken();
      }
      // get customer card on file
      this.getCustomerCreditCards();
    },

    async getCustomerStaxIdToken() {
      try {
        let { data } = await getCustomerTokenApi({
          accountId: this.isClinic ? this.clinicId : this.customerId,
          accountType: this.isClinic ? "clinic" : "customer",
          clinicId: this.isClinic ? this.clinicId : null,
          tokenPlatform: "stax"
        });
        this.customerStaxIdToken = data.customer_token;
      } catch(err) {
        console.log('get customer stax id token err', err);
        this.customerStaxIdToken = "";
      }
    },

    async getCustomerCreditCards() {
      try {
        this.creditCardList = [];
        this.getCreditCardsLoading = true;
        let { data } = await getSavedPaymentsApi({
          customerId: this.customerId,
          clinicId: this.clinicId,
          methodType: "card"
        });

        let tokenPlatform = this.isUsCustomer ? "stax" : "stripe";
        let cards = [];
        if (data) {
          let customerCards = (data?.customer_payment_methods || [])
            .filter(c => c.token_platform === tokenPlatform)
            .map(c => ({ ...c, isClinicCard: false }));
          let clinicCards = (data?.clinic_payment_methods || [])
            .filter(c => c.token_platform === tokenPlatform)
            .map(c => ({ ...c, isClinicCard: true }));
          cards = customerCards.concat(clinicCards);
        }
        cards.forEach(card => {
          card.is_select = false;
          card.card_exp = card.expiry_date;
          card.card_last_four = card.last_four;
          this.creditCardList.push(card);
        });
        this.getCreditCardsLoading = false;
      } catch(err) {
        this.getCreditCardsLoading = false;
      }
    },

    handleClickCard(clickedCard) {
      // select current card, and de-select remaining cards
      this.creditCardList.forEach((card) => {
        card.is_select = card.id === clickedCard.id ? true : false;
      });
    },

    removeCard(card) {
      // remove card from current cardList
      let index = this.creditCardList.indexOf(card);
      this.creditCardList.splice(index, 1);
      if (this.creditCardList.length > 0) {
        this.creditCardList[0].is_select = true;
      }
    },

    updateCard(updatedCard) {
      let card = this.creditCardList.find((c) => c.id === updatedCard.id);
      card.is_default = updatedCard.is_default;
      card.card_exp = updatedCard.card_exp;
    },

    // pay by selected customer's saved card or net pay
    completePaymentWithSavedCard() {
      let paymentInfo = {
        isUseNetPay: this.isUseNetPay,
        isUseCustomerCredit: this.isUseCustomerCredit,
        isSaveCard: true,
        amount: this.chargeAmount,
        type: 'card'
      }
      if (!this.isUseNetPay) {
        // customer use saved card to complete payment
        // get the selected card
        let selectedCard = this.creditCardList.find((card) => card.is_select);
        if (selectedCard) {
          if (selectedCard.token_platform === "stripe") {
            this.payWithSavedStripeCard(selectedCard);
            return;
          }
        }
        paymentInfo.staxToken = selectedCard || { payment_token: null };
      }   
      this.$emit("completePayment", paymentInfo);
    },

    /* staxInfo = {
        staxTokenId: String,
        isSaveCard: Boolean
      }
    */
    // pay by customer's new card
    tokenizeCard(staxInfo) {
      if (this.mode === "payment") {
        let paymentInfo = {
          ...staxInfo,
          isUseNetPay: this.isUseNetPay,
          isUseCustomerCredit: this.isUseCustomerCredit
        }
        this.$emit("completePayment", paymentInfo);
      } else {
        this.creditCardList.push({ ...staxInfo.staxToken, isClinicCard: this.isClinic ? true : false });
        this.isAddNewCard = false;
      }
    },
    
    handleApplyCredit(amount) {
      this.$emit("changeUseCustomerCredit", true, amount);
      console.log(amount,'amount in the customer pay component')
    },
    
    handleResetCredit() {
      this.$emit("changeUseCustomerCredit", false);
    },
    handleUseNetPay() {
      this.isUseNetPay = !this.isUseNetPay;
      this.$emit("changeUseNetPay", this.isUseNetPay);
    },
    completeAchPayment(cardPaymentInfo) {
      let paymentInfo = {
        ...cardPaymentInfo,
        isUseNetPay: false,
        isUseCustomerCredit: false,
        isAchPayment: cardPaymentInfo.paymentId ? true : false
      };
      this.$emit("completePayment", paymentInfo);
    },

    switchToAchPayment() {
      this.paymentMethod = "ach";
      // currently make sure credit & net pay is not in used when user switch to ach
      this.isUseCustomerCredit = false;
      this.$emit("changeUseCustomerCredit", false);
      this.isUseNetPay = false;
      this.$emit("changeUseNetPay", false);
    },
    async completeStripePayment(stripePaymentInfo) {
      if (this.mode === "payment") {
        let paymentInfo = {
          ...stripePaymentInfo,
          isUseNetPay: this.isUseNetPay,
          isUseCustomerCredit: this.isUseCustomerCredit,
          platform: "stripe"
        };
        this.$emit("completePayment", paymentInfo);
      } else {
        this.isAddNewCard = false;
        await this.getCustomerCreditCards();
      }
    },
    async payWithSavedStripeCard(card) {
      if (this.chargeAmount === 0) {
        let paymentInfo = {
          paymentId: null,
          sampleId: null,
          julienBarcode: null,
          orderId: null,
          isUseNetPay: this.isUseNetPay,
          isUseCustomerCredit: this.isUseCustomerCredit,
          platform: "stripe"
        };
        if (this.chargeType === "kitorder") {
          let generateOrderIdResp = await generateOrderIdApi();
          let kitOrderId = generateOrderIdResp.data.order_id;
          paymentInfo.orderId = kitOrderId;
        }
        if (this.chargeType === "testorder") {
          let orderSamplePriceMaps = await getOrderSamplePriceMaps(this.orderPriceList);
          paymentInfo.orderSamplePriceMaps = orderSamplePriceMaps;
        }
        this.$emit('completePayment', paymentInfo);
        return;
      }
      try {
        this.payWithSavedCardLoading = true;
        if (!this.stripeCardPaymentInfo) {
          // not call api again if already have payment info, avoid duplicate payment
          let recordId = await this.getRecordId();
          if (this.chargeType === "kitorder") {
            this.stripeCardPaymentInfo = await this.getStripePaymentInfoForSavedCard(recordId, card);
          } else {
            let samples = [];
            let orderSamplePriceMaps = { sampleIdJulienBarcodeMap: {}, samplePriceMap: {} };
            if (this.sampleList.length > 0) {
              samples = this.sampleList;
            } else {
              let { sampleIdJulienBarcodeMap, samplePriceMap } = await getOrderSamplePriceMaps(this.orderPriceList);
              Object.keys(sampleIdJulienBarcodeMap).forEach(sampleId => {
                samples.push({
                  orderId: null,
                  sample_id: sampleId,
                  balance: samplePriceMap[sampleId]
                });
              });
              orderSamplePriceMaps = { sampleIdJulienBarcodeMap, samplePriceMap };
            }
            let stripeChargeResp = await this.callPayOrdersApi(recordId, card, samples);
            let { sample_id, julien_barcode, record_id, next_action } = stripeChargeResp.data;
            this.stripeCardPaymentInfo = {
              paymentId: record_id,
              sampleId: sample_id,
              julienBarcode: julien_barcode,
              orderId: stripeChargeResp.orderId,
              platform: "stripe",
              nextAction: next_action,
              orderSamplePriceMaps,
              amount: this.chargeAmount,
              type: 'card',
            };
          }
        }
        if (this.stripeCardPaymentInfo.nextAction) {
          notify({ type: 'error', title: 'Payment Authentication Required', message: "We're sorry, but we encountered an issue processing your payment. Please try again in a few minutes or use a different credit card. If the problem persists, contact your bank or our support team." });
          this.stripeCardPaymentInfo = null;
          this.payWithSavedCardLoading = false;
          return;
        }
        let paymentInfo = {
          ...this.stripeCardPaymentInfo,
          isUseNetPay: this.isUseNetPay,
          isUseCustomerCredit: this.isUseCustomerCredit,
          platform: "stripe"
        };
        this.payWithSavedCardLoading = false;
        this.$emit('completePayment', paymentInfo);
      } catch (err) {
        this.payWithSavedCardLoading = false;
        console.log('pay with saved stripe card error', err);
      }
    },
    async getRecordId() {
      let recordId = null;
      // create payment intent to get record id
      let token_platform = this.stripeAccount === "ZB" ? "stripe_zb" : "stripe";
      let paymentBody = {
        amount: Number(this.chargeAmount.toFixed(2)),
        currency: this.currency.name.toLowerCase(),
        token_platform,
        payment_type: "card"
      };
      let paymentIntentResp = await createPaymentIntent(paymentBody);
      recordId = paymentIntentResp.data.record_id;
      return recordId;
    },

    async getStripePaymentInfoForSavedCard(recordId, card) {
      let token_platform = this.stripeAccount === "ZB" ? "stripe_zb" : "stripe";
      let payload = {
        // account must match the card's owner: the saved payment token only works
        // against the Stripe customer it was created under, not the session role
        account_id: card.isClinicCard ? this.clinicId : this.customerId,
        account_type: card.isClinicCard ? "clinic" : "customer",
        amount: Number(this.chargeAmount.toFixed(2)),
        currency:  this.currency.name.toLowerCase(),
        charge_type: this.chargeType,
        charge_type_id: this.orderId ? this.orderId : null,
        type: "card",
        token_platform,
        payment_source: "portal",
        payment_token: card.payment_token,
        notes: "",
        record_id: recordId,
        new_sample: this.needSampleId,
        using_default: false,
        saving_for_later: true,
        sample_id: this.sampleId ? Number(this.sampleId) : null,
        clinic_id: this.clinicId
      }
      if (this.chargeType === "kitorder") {
        let generateOrderIdResp = await generateOrderIdApi();
        let kitOrderId = generateOrderIdResp.data.order_id;
        payload.charge_type_id = kitOrderId;
      }
      let res = await chargeApi(payload);
      let { sample_id, julien_barcode, record_id, next_action } = res.data;
      let paymentInfo = {
        paymentId: record_id,
        sampleId: sample_id,
        julienBarcode: julien_barcode,
        orderId: payload.charge_type_id,
        nextAction: next_action
      };
      return paymentInfo
    },

    async callPayOrdersApi(recordId, card, sampleList) {
      let chargeDetails = getChargeDetailsForPayOrdersApi(sampleList, this.isUseCustomerCredit, this.customerCredit, this.chargeType, this.usedCredit);
      let token_platform = this.stripeAccount === "ZB" ? "stripe_zb" : "stripe";
      let totalAmount = this.chargeAmount;
      try {
        if (Array.isArray(sampleList) && sampleList.length > 0) {
          totalAmount = sampleList.reduce((sum, s) => {
            const val = (s.price !== undefined ? s.price : (s.balance !== undefined ? s.balance : 0));
            return sum + Number(val || 0);
          }, 0);
        }
      } catch (e) {
        totalAmount = this.chargeAmount;
      }
      let payOrdersResp = await payOrdersApi({
        // account must match the card's owner: the saved payment token only works
        // against the Stripe customer it was created under, not the session role
        account_id: card.isClinicCard ? this.clinicId : this.customerId,
        account_type: card.isClinicCard ? "clinic" : "customer",
        amount: Number(Number(totalAmount).toFixed(2)),
        currency: this.currency.name.toLowerCase(),
        charge_details: chargeDetails,
        type: 'card',
        token_platform,
        payment_token: card.payment_token,
        payment_source: 'portal',
        record_id: recordId,
        without_apply: this.payOrderWithoutApply
      });
      return payOrdersResp;
    },

    // show edit and delete button for following cases:
    // 1. mode is not display
    // 2. if user is a clinic admin, then show edit and delete button for both customer and clinic cards
    // 3. if user is a customer, then show edit and delete button for customer cards only
    isShowEditAndDelete(card) {
      if (this.mode === 'display') return false;
      if (this.isClinic) {
        return true;
      } else {
        return !card.isClinicCard;
      }
    }

  }
}
</script>

<style scoped>
.pay-text {
  height: 19px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.46px;
  line-height: 19px;
}
.bottom-fixed {
  position: fixed;
  bottom: 89px; 
  z-index: 1000; 
  width: 373px;
  height: 42px;
  right: 52px;
}
</style>